import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import {
  ArrowRight,
  BarChart3,
  BookOpen,
  Check,
  ChevronRight,
  FileText,
  Layers3,
  Menu,
  Moon,
  Sparkles,
  Sun,
  Target,
  Timer,
  X,
} from "lucide-react";
import { useAuth } from "../context/AuthContext";
import { useTheme } from "../context/ThemeContext";
import { LoginForm } from "../components/login-form";
import { SignupForm } from "../components/signup-form";
import SimulatorPreview from "../components/SimulatorPreview";

const features = [
  {
    number: "01",
    icon: BookOpen,
    title: "1,000+ original questions",
    copy: "Filter by topic, paper, and difficulty. Every question comes with a complete worked solution.",
  },
  {
    number: "02",
    icon: FileText,
    title: "Every official TMUA paper",
    copy: "Sit papers from 2016–2026 in timed or untimed mode, with recent sittings available in Pro.",
  },
  {
    number: "03",
    icon: Timer,
    title: "A 1:1 PearsonVUE simulator",
    copy: "Practise the exact timing, flagging, navigation, and interface you will use on exam day.",
  },
  {
    number: "04",
    icon: BarChart3,
    title: "See what costs you marks",
    copy: "Track accuracy, time per question, topic performance, and every previous incorrect answer.",
  },
  {
    number: "05",
    icon: Target,
    title: "Build the session you need",
    copy: "Choose the source, paper, topics, mode, and number of questions instead of using a generic quiz.",
  },
  {
    number: "06",
    icon: Layers3,
    title: "Complete community papers",
    copy: "Add full contributed papers to your practice without mixing them into an unstructured question feed.",
  },
];

const steps = [
  ["Build a session", "Choose question bank, official, or community questions—then set your topics and timing."],
  ["Answer under pressure", "Use the modern workspace or rehearse the exact PearsonVUE exam interface."],
  ["Study the solution", "See the complete method after each question and understand where your approach failed."],
  ["Target the next weakness", "Use topic accuracy and timing data to decide exactly what to practise next."],
];

const testimonials = [
  {
    quote: "The questions felt remarkably close to the real exam. It made the actual sitting feel familiar.",
    name: "Muhammad A.",
    detail: "9.0 · October 2025",
  },
  {
    quote: "I had three weeks and needed a clear plan. The question bank showed me exactly where to focus.",
    name: "Ryan S.",
    detail: "7.5 · October 2025",
  },
  {
    quote: "The simulator removed the uncertainty. By exam day, the format and timing were second nature.",
    name: "Sasha K.",
    detail: "8.4 · January 2026",
  },
];

const tiers = [
  {
    name: "Free",
    price: "£0",
    note: "Official papers and daily practice, free.",
    features: ["20 questions each day", "Official past papers", "Exam simulator", "Performance overview"],
    cta: "Start practising",
  },
  {
    name: "Pro",
    price: "£19.99",
    period: "/ month",
    note: "The complete bank and every solution.",
    features: ["Full 1,000+ question bank", "Latest locked papers", "Every worked solution", "Spaced repetition"],
    cta: "Try Pro free",
    featured: true,
  },
];

function Brand({ onClick }) {
  return (
    <button onClick={onClick} className="brand-btn" aria-label="tmuaprep home">
      tmua<span className="brand-prep">prep</span>
    </button>
  );
}

function ThemeButton() {
  const { theme, toggleTheme } = useTheme();
  return (
    <button
      onClick={toggleTheme}
      className="flex h-9 w-9 items-center justify-center rounded-full border border-[var(--border-ui)] bg-[var(--surface)] text-[var(--muted-text)] transition hover:border-[var(--border-strong)] hover:text-[var(--text)]"
      aria-label={`Switch to ${theme === "dark" ? "light" : "dark"} mode`}
    >
      {theme === "dark" ? <Sun size={15} /> : <Moon size={15} />}
    </button>
  );
}

const demoQuestions = [
  {
    id: "stationary-points",
    topic: "Algebra",
    difficulty: "Medium",
    prompt: <>The curve <span className="font-landing-display italic text-[var(--text)]">y = x³ − 6x² + 9x + 1</span> has two stationary points. For which values of <span className="font-landing-display italic text-[var(--text)]">k</span> does <span className="font-landing-display italic text-[var(--text)]">f(x) = k</span> have exactly one real solution?</>,
    options: ["k < 1", "k > 5", "k < 1 or k > 5", "1 < k < 5"],
    correct: 2,
    solution: "The stationary values are 5 and 1. A horizontal line meets the curve once only when it lies below 1 or above 5.",
  },
  {
    id: "sequence",
    topic: "Sequences",
    difficulty: "Warm-up",
    prompt: <>An arithmetic sequence has first term <span className="font-landing-display italic text-[var(--text)]">3</span> and common difference <span className="font-landing-display italic text-[var(--text)]">4</span>. What is its tenth term?</>,
    options: ["35", "39", "40", "43"],
    correct: 1,
    solution: "Use aₙ = a₁ + (n − 1)d. Therefore a₁₀ = 3 + 9 × 4 = 39.",
  },
  {
    id: "probability",
    topic: "Probability",
    difficulty: "Medium",
    prompt: <>A fair die is rolled twice. What is the probability that the sum of the two scores is <span className="font-landing-display italic text-[var(--text)]">8</span>?</>,
    options: ["1/9", "5/36", "1/6", "7/36"],
    correct: 1,
    solution: "There are 36 equally likely outcomes. Five give a total of 8: (2,6), (3,5), (4,4), (5,3), and (6,2).",
  },
];

function DashboardPreview({ originalSimulator = false }) {
  const [questionIndex, setQuestionIndex] = useState(0);
  const [answers, setAnswers] = useState({});
  const [checked, setChecked] = useState({});
  const question = demoQuestions[questionIndex];
  const selected = answers[question.id];
  const isChecked = checked[question.id] === true;
  const isCorrect = isChecked && selected === question.correct;
  const completed = demoQuestions.filter(item => checked[item.id]).length;
  const correct = demoQuestions.filter(item => checked[item.id] && answers[item.id] === item.correct).length;
  const accuracy = completed ? `${Math.round((correct / completed) * 100)}%` : "—";

  const chooseAnswer = index => {
    if (!isChecked) setAnswers(current => ({ ...current, [question.id]: index }));
  };

  const checkAnswer = () => {
    if (selected !== undefined) setChecked(current => ({ ...current, [question.id]: true }));
  };

  const retryQuestion = () => {
    setAnswers(current => {
      const next = { ...current };
      delete next[question.id];
      return next;
    });
    setChecked(current => {
      const next = { ...current };
      delete next[question.id];
      return next;
    });
  };

  if (originalSimulator) {
    return (
      <div className="relative mx-auto mt-16 max-w-5xl px-1 sm:px-4">
        <div className="overflow-hidden rounded-[30px] border border-[var(--border-strong)] bg-[var(--surface)] shadow-[var(--shadow)]">
          <div className="grid gap-5 border-b border-[var(--border-ui)] px-6 py-5 text-left sm:px-8 md:grid-cols-[1fr_auto] md:items-end">
            <div>
              <p className="text-xs font-semibold text-blue-500">Interactive exam simulator</p>
              <h2 className="font-landing-display mt-2 text-2xl tracking-tight sm:text-3xl">Try the real exam interface now.</h2>
              <p className="mt-2 max-w-2xl text-sm leading-6 text-[var(--muted-text)]">Answer the sample question, flag it, open the navigator, and move through the paper exactly as you will on test day.</p>
            </div>
            <span className="rounded-full border border-[var(--border-ui)] bg-[var(--soft-fill)] px-3 py-1.5 text-[10px] font-semibold text-[var(--subtle-text)]">Interactive preview</span>
          </div>
          <div className="bg-[var(--surface-raised)] pt-6"><SimulatorPreview /></div>
        </div>
        <div className="absolute -bottom-8 left-1/2 -z-10 h-32 w-2/3 -translate-x-1/2 rounded-full bg-blue-500/10 blur-3xl" />
      </div>
    );
  }

  return (
    <div className="relative mx-auto mt-16 max-w-5xl px-1 sm:px-4">
      <div className="overflow-hidden rounded-[30px] border border-[var(--border-strong)] bg-[var(--surface)] shadow-[var(--shadow)]">
        <div className="flex flex-wrap items-center justify-between gap-3 border-b border-[var(--border-ui)] px-5 py-4 sm:px-7">
          <div className="flex items-center gap-3">
            <span className="relative flex h-2 w-2"><span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-emerald-400 opacity-60" /><span className="relative h-2 w-2 rounded-full bg-emerald-400" /></span>
            <span className="text-xs font-semibold text-[var(--text-soft)]">Interactive exam preview</span>
          </div>
          <div className="flex items-center gap-2">
            {demoQuestions.map((item, index) => (
              <button
                key={item.id}
                onClick={() => setQuestionIndex(index)}
                aria-label={`Open example question ${index + 1}`}
                className={`flex h-7 w-7 items-center justify-center rounded-full border text-[10px] font-semibold transition ${index === questionIndex ? "border-blue-500 bg-blue-500 text-white" : checked[item.id] ? "border-emerald-500/40 bg-emerald-500/10 text-emerald-500" : "border-[var(--border-ui)] bg-[var(--soft-fill)] text-[var(--muted-text)] hover:border-[var(--border-strong)]"}`}
              >
                {checked[item.id] && answers[item.id] === item.correct ? <Check size={11} strokeWidth={2.5} /> : index + 1}
              </button>
            ))}
          </div>
        </div>

        <div className="grid lg:grid-cols-[1.42fr_.58fr]">
          <div className="border-b border-[var(--border-ui)] p-6 sm:p-9 lg:border-b-0 lg:border-r">
            <div className="mb-6 flex items-center justify-between gap-4">
              <span className="text-xs font-semibold text-blue-500">{question.topic} · {question.difficulty}</span>
              <span className="rounded-full bg-[var(--soft-fill)] px-3 py-1 font-mono text-[10px] text-[var(--subtle-text)]">Question {questionIndex + 1} of {demoQuestions.length}</span>
            </div>
            <p className="min-h-[84px] max-w-2xl text-base leading-7 text-[var(--text-soft)] sm:text-lg">{question.prompt}</p>

            <div className="mt-7 grid gap-2.5 sm:grid-cols-2">
              {question.options.map((option, index) => {
                const chosen = selected === index;
                const correctOption = isChecked && index === question.correct;
                const wrongOption = isChecked && chosen && index !== question.correct;
                const stateClass = correctOption
                  ? "border-emerald-500/60 bg-emerald-500/10 text-[var(--text)]"
                  : wrongOption
                    ? "border-red-500/60 bg-red-500/10 text-[var(--text)]"
                    : chosen
                      ? "border-blue-500 bg-blue-500/10 text-[var(--text)]"
                      : "border-[var(--border-ui)] bg-[var(--soft-fill)] text-[var(--text-soft)] hover:border-blue-400/60 hover:bg-blue-500/[0.06]";
                return (
                  <button
                    key={option}
                    onClick={() => chooseAnswer(index)}
                    disabled={isChecked}
                    aria-pressed={chosen}
                    className={`flex min-h-12 items-center gap-3 rounded-2xl border px-4 py-3 text-left text-sm transition ${stateClass} ${isChecked ? "cursor-default" : "cursor-pointer"}`}
                  >
                    <span className={`flex h-6 w-6 shrink-0 items-center justify-center rounded-full text-[10px] font-semibold ${correctOption ? "bg-emerald-500 text-white" : wrongOption ? "bg-red-500 text-white" : chosen ? "bg-blue-500 text-white" : "bg-[var(--surface)] text-[var(--muted-text)]"}`}>
                      {correctOption ? <Check size={11} strokeWidth={2.5} /> : String.fromCharCode(65 + index)}
                    </span>
                    {option}
                  </button>
                );
              })}
            </div>

            <div className="mt-6 flex flex-wrap items-center justify-between gap-3 border-t border-[var(--border-ui)] pt-5">
              <button
                onClick={() => setQuestionIndex(index => Math.max(0, index - 1))}
                disabled={questionIndex === 0}
                className="btn-ghost disabled:cursor-not-allowed disabled:opacity-30"
              >
                ← Previous
              </button>
              <div className="flex items-center gap-2">
                {isChecked && <button onClick={retryQuestion} className="btn-secondary !px-4 !py-2">Try again</button>}
                {!isChecked ? (
                  <button onClick={checkAnswer} disabled={selected === undefined} className="btn-primary !px-5 !py-2.5">Check answer <Check size={13} /></button>
                ) : (
                  <button onClick={() => setQuestionIndex(index => Math.min(demoQuestions.length - 1, index + 1))} disabled={questionIndex === demoQuestions.length - 1} className="btn-primary !px-5 !py-2.5 disabled:opacity-40">Next question <ArrowRight size={13} /></button>
                )}
              </div>
            </div>
          </div>

          <aside className="flex flex-col bg-[var(--surface-raised)] p-6 sm:p-8">
            <div className="flex items-start justify-between">
              <div>
                <p className="text-xs text-[var(--subtle-text)]">Your demo accuracy</p>
                <p className="mt-1 text-3xl font-semibold tracking-tight text-[var(--text)]">{accuracy}</p>
              </div>
              <div className="flex h-10 w-10 items-center justify-center rounded-full bg-blue-500/10 text-blue-500"><BarChart3 size={17} /></div>
            </div>
            <div className="mt-7">
              <div className="flex items-center justify-between text-xs"><span className="font-semibold text-[var(--text-soft)]">Progress</span><span className="text-[var(--subtle-text)]">{completed}/{demoQuestions.length} checked</span></div>
              <div className="mt-3 h-1.5 overflow-hidden rounded-full bg-[var(--soft-fill-strong)]"><div className="h-full rounded-full bg-blue-500 transition-all duration-300" style={{ width: `${(completed / demoQuestions.length) * 100}%` }} /></div>
            </div>

            <div className={`mt-8 flex-1 rounded-2xl border p-5 transition-colors ${isChecked ? isCorrect ? "border-emerald-500/30 bg-emerald-500/[0.07]" : "border-red-500/30 bg-red-500/[0.06]" : "border-[var(--border-ui)] bg-[var(--surface)]"}`} aria-live="polite">
              {isChecked ? (
                <>
                  <div className={`flex items-center gap-2 text-sm font-semibold ${isCorrect ? "text-emerald-500" : "text-red-500"}`}>
                    {isCorrect ? <Check size={15} /> : <X size={15} />} {isCorrect ? "Correct" : "Not quite"}
                  </div>
                  <p className="mt-3 text-xs leading-5 text-[var(--text-soft)]">{question.solution}</p>
                </>
              ) : (
                <>
                  <Sparkles size={16} className="text-blue-500" />
                  <p className="mt-3 text-sm font-semibold text-[var(--text)]">Try it yourself</p>
                  <p className="mt-2 text-xs leading-5 text-[var(--muted-text)]">Choose an answer, check it, then move between the example questions above.</p>
                </>
              )}
            </div>
          </aside>
        </div>
      </div>
      <div className="absolute -bottom-8 left-1/2 -z-10 h-32 w-2/3 -translate-x-1/2 rounded-full bg-blue-500/10 blur-3xl" />
    </div>
  );
}

export default function TMUAPrep() {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [showSignIn, setShowSignIn] = useState(false);
  const [modalView, setModalView] = useState("signup");
  const [signingIn, setSigningIn] = useState(false);
  const navigate = useNavigate();
  const { user, signInWithGoogle } = useAuth();

  const handleTryFree = () => {
    if (user) navigate("/practice");
    else setShowSignIn(true);
  };

  const handleGoogleSignIn = async () => {
    setSigningIn(true);
    try {
      await signInWithGoogle();
      setShowSignIn(false);
      navigate("/practice");
    } catch (error) {
      toast.error(error?.message || "Sign-in failed");
    } finally {
      setSigningIn(false);
    }
  };

  return (
    <div className="min-h-screen overflow-hidden bg-[var(--bg)] text-[var(--text)] transition-colors duration-300">
      {showSignIn && (
        <div className="fixed inset-0 z-[100] flex items-center justify-center bg-[var(--overlay)] p-4 backdrop-blur-md" onClick={() => setShowSignIn(false)}>
          <div className="w-full max-w-sm animate-[scaleIn_0.2s_ease]" onClick={event => event.stopPropagation()}>
            {modalView === "login" ? (
              <LoginForm onGoogleLogin={handleGoogleSignIn} signingIn={signingIn} onSwitchToSignup={() => setModalView("signup")} />
            ) : (
              <SignupForm onGoogleLogin={handleGoogleSignIn} signingIn={signingIn} onSwitchToLogin={() => setModalView("login")} />
            )}
          </div>
        </div>
      )}

      <header className="fixed inset-x-0 top-0 z-50 px-4 pt-4 sm:px-6">
        <nav className="mx-auto flex h-14 max-w-6xl items-center justify-between rounded-full border border-[var(--border-ui)] bg-[color:var(--overlay)] px-5 shadow-sm backdrop-blur-xl">
          <Brand onClick={() => window.scrollTo({ top: 0, behavior: "smooth" })} />
          <div className="hidden items-center gap-7 md:flex">
            <a href="#platform" className="text-xs font-medium text-[var(--muted-text)] transition hover:text-[var(--text)]">Platform</a>
            <a href="#method" className="text-xs font-medium text-[var(--muted-text)] transition hover:text-[var(--text)]">How it works</a>
            <a href="#pricing" className="text-xs font-medium text-[var(--muted-text)] transition hover:text-[var(--text)]">Pricing</a>
          </div>
          <div className="hidden items-center gap-2 md:flex">
            <ThemeButton />
            <button onClick={handleTryFree} className="btn-primary !px-5 !py-2.5">Try free <ArrowRight size={13} /></button>
          </div>
          <button className="flex h-9 w-9 items-center justify-center rounded-full border border-[var(--border-ui)] md:hidden" onClick={() => setMobileMenuOpen(open => !open)} aria-label="Open menu">
            {mobileMenuOpen ? <X size={16} /> : <Menu size={16} />}
          </button>
        </nav>
        {mobileMenuOpen && (
          <div className="mx-auto mt-2 max-w-6xl rounded-3xl border border-[var(--border-ui)] bg-[var(--surface)] p-5 shadow-[var(--shadow)] md:hidden">
            <div className="space-y-1">
              {[['Platform', '#platform'], ['How it works', '#method'], ['Pricing', '#pricing']].map(([label, href]) => (
                <a key={label} href={href} onClick={() => setMobileMenuOpen(false)} className="block rounded-xl px-3 py-2.5 text-sm text-[var(--muted-text)] hover:bg-[var(--soft-fill)] hover:text-[var(--text)]">{label}</a>
              ))}
            </div>
            <div className="mt-4 flex gap-2 border-t border-[var(--border-ui)] pt-4">
              <ThemeButton />
              <button onClick={handleTryFree} className="btn-primary flex-1">Try free <ArrowRight size={13} /></button>
            </div>
          </div>
        )}
      </header>

      <main>
        <section className="paper-grid px-5 pb-20 pt-32 sm:pt-36">
          <div className="mx-auto max-w-5xl text-center">
            <p className="mb-5 text-xs font-semibold text-blue-500">Complete TMUA preparation</p>
            <h1 className="font-landing-display text-[44px] leading-[1.03] tracking-[-0.035em] sm:text-[58px] lg:text-[66px]">
              1,000+ TMUA questions.<br /><span className="text-blue-500">Past papers and realistic mocks.</span>
            </h1>
            <p className="mx-auto mt-6 max-w-2xl text-base leading-7 text-[var(--muted-text)] sm:text-lg">
              Practise by topic, sit every official paper in a 1:1 PearsonVUE simulator, review worked solutions, and track exactly where you lose marks.
            </p>
            <div className="mt-8 flex flex-col items-center justify-center gap-3 sm:flex-row">
              <button onClick={handleTryFree} className="btn-primary min-w-44 !py-3.5 !text-sm">Start practising <ArrowRight size={15} /></button>
              <a href="#simulator" className="btn-secondary min-w-44 !py-3.5 !text-sm">Try the simulator</a>
            </div>
            <div className="mx-auto mt-10 grid max-w-3xl overflow-hidden rounded-2xl border border-[var(--border-ui)] bg-[var(--surface)] text-left sm:grid-cols-3">
              <div className="border-b border-[var(--border-ui)] px-5 py-4 sm:border-b-0 sm:border-r"><div className="num text-xl font-semibold text-[var(--text)]">1,000+</div><div className="mt-1 text-[11px] text-[var(--muted-text)]">Original practice questions</div></div>
              <div className="border-b border-[var(--border-ui)] px-5 py-4 sm:border-b-0 sm:border-r"><div className="num text-xl font-semibold text-[var(--text)]">2016–2026</div><div className="mt-1 text-[11px] text-[var(--muted-text)]">Official TMUA papers</div></div>
              <div className="px-5 py-4"><div className="num text-xl font-semibold text-[var(--text)]">1:1</div><div className="mt-1 text-[11px] text-[var(--muted-text)]">PearsonVUE exam simulator</div></div>
            </div>
          </div>
          <div id="simulator"><DashboardPreview originalSimulator /></div>
        </section>

        <section id="platform" className="border-t border-[var(--border-ui)] px-5 py-24 sm:py-32">
          <div className="mx-auto max-w-6xl">
            <div className="grid gap-8 md:grid-cols-[.8fr_1.2fr] md:items-end">
              <p className="text-xs font-semibold text-blue-500">Inside tmuaprep</p>
              <div>
                <h2 className="font-landing-display text-4xl leading-tight sm:text-5xl">Question bank, official papers, mock exams, and analytics.</h2>
                <p className="mt-5 max-w-2xl text-sm leading-6 text-[var(--muted-text)]">One place to practise, simulate the real exam, review every solution, and improve your weakest topics.</p>
              </div>
            </div>
            <div className="mt-16 grid border-l border-t border-[var(--border-ui)] sm:grid-cols-2 lg:grid-cols-3">
              {features.map(({ number, icon: Icon, title, copy }) => (
                <article key={number} className="group min-h-64 border-b border-r border-[var(--border-ui)] bg-[var(--surface)] p-7 transition hover:bg-[var(--surface-raised)] sm:p-8">
                  <div className="flex items-center justify-between">
                    <span className="font-mono text-[10px] text-[var(--subtle-text)]">{number}</span>
                    <Icon size={18} strokeWidth={1.5} className="text-blue-400" />
                  </div>
                  <h3 className="mt-16 font-landing-display text-2xl">{title}</h3>
                  <p className="mt-3 text-sm leading-6 text-[var(--muted-text)]">{copy}</p>
                </article>
              ))}
            </div>
          </div>
        </section>

        <section id="method" className="bg-[var(--surface)] px-5 py-24 sm:py-32">
          <div className="mx-auto max-w-6xl">
            <div className="max-w-2xl">
              <p className="text-xs font-semibold text-blue-500">How it works</p>
              <h2 className="mt-5 font-landing-display text-4xl leading-tight sm:text-5xl">Pick questions. Sit the exam. Review every mistake.</h2>
            </div>
            <div className="mt-16 grid gap-3 md:grid-cols-4">
              {steps.map(([title, copy], index) => (
                <div key={title} className="rounded-3xl border border-[var(--border-ui)] bg-[var(--bg)] p-6">
                  <div className="flex h-9 w-9 items-center justify-center rounded-full border border-[var(--border-ui)] font-mono text-[10px] text-blue-400">{index + 1}</div>
                  <h3 className="mt-10 text-sm font-semibold">{title}</h3>
                  <p className="mt-2 text-xs leading-5 text-[var(--muted-text)]">{copy}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        <section className="border-y border-[var(--border-ui)] px-5 py-24 sm:py-32">
          <div className="mx-auto max-w-6xl">
            <div className="text-center">
              <p className="text-xs font-semibold text-blue-400">Student results</p>
              <h2 className="mt-5 font-landing-display text-4xl sm:text-5xl">Students used it. Then sat the real thing.</h2>
            </div>
            <div className="mt-14 grid gap-4 md:grid-cols-3">
              {testimonials.map(testimonial => (
                <figure key={testimonial.name} className="flex min-h-72 flex-col justify-between rounded-[28px] border border-[var(--border-ui)] bg-[var(--surface)] p-7">
                  <blockquote className="font-landing-display text-xl leading-8 text-[var(--text-soft)]">“{testimonial.quote}”</blockquote>
                  <figcaption className="mt-10 border-t border-[var(--border-ui)] pt-5">
                    <p className="text-xs font-semibold">{testimonial.name}</p>
                    <p className="mt-1 text-[11px] text-[var(--subtle-text)]">{testimonial.detail}</p>
                  </figcaption>
                </figure>
              ))}
            </div>
          </div>
        </section>

        <section id="pricing" className="px-5 py-24 sm:py-32">
          <div className="mx-auto max-w-5xl">
            <div className="text-center">
              <p className="text-xs font-semibold text-blue-400">Simple pricing</p>
              <h2 className="mt-5 font-landing-display text-4xl sm:text-5xl">Use the core product free. Unlock the full bank when you need it.</h2>
            </div>
            <div className="mx-auto mt-14 grid max-w-3xl gap-4 md:grid-cols-2">
              {tiers.map(tier => (
                <article key={tier.name} className={`relative rounded-[30px] border p-7 ${tier.featured ? "border-blue-400/50 bg-blue-500/[0.07]" : "border-[var(--border-ui)] bg-[var(--surface)]"}`}>
                  {tier.featured && <span className="absolute right-6 top-6 rounded-full bg-blue-500 px-3 py-1 text-[10px] font-semibold text-white">Most popular</span>}
                  <p className="text-sm font-semibold">{tier.name}</p>
                  <div className="mt-8 flex items-end gap-2"><span className="font-landing-display text-5xl">{tier.price}</span>{tier.period && <span className="pb-1 text-xs text-[var(--muted-text)]">{tier.period}</span>}</div>
                  <p className="mt-3 text-xs text-[var(--muted-text)]">{tier.note}</p>
                  <ul className="my-8 space-y-3 border-y border-[var(--border-ui)] py-7">
                    {tier.features.map(feature => <li key={feature} className="flex items-center gap-3 text-xs text-[var(--text-soft)]"><Check size={14} className="text-blue-400" />{feature}</li>)}
                  </ul>
                  <button onClick={handleTryFree} className={tier.featured ? "btn-primary w-full" : "btn-secondary w-full"}>{tier.cta}<ChevronRight size={14} /></button>
                </article>
              ))}
            </div>
          </div>
        </section>

        <section className="px-5 pb-20">
          <div className="mx-auto max-w-6xl overflow-hidden rounded-[36px] bg-blue-500 px-6 py-16 text-center text-white sm:px-16 sm:py-20">
            <p className="text-xs font-semibold text-blue-100">Start free</p>
            <h2 className="mx-auto mt-5 max-w-3xl font-landing-display text-4xl leading-tight sm:text-6xl">Try 20 TMUA questions today.</h2>
            <button onClick={handleTryFree} className="mt-8 inline-flex items-center gap-2 rounded-full bg-white px-6 py-3.5 text-sm font-semibold text-zinc-900 transition hover:-translate-y-0.5 hover:bg-blue-50">Start practising <ArrowRight size={15} /></button>
          </div>
        </section>
      </main>

      <footer className="border-t border-[var(--border-ui)] px-5 py-10">
        <div className="mx-auto flex max-w-6xl flex-col gap-6 sm:flex-row sm:items-center sm:justify-between">
          <Brand />
          <p className="text-[11px] text-[var(--subtle-text)]">Independent TMUA preparation. Not affiliated with UAT-UK.</p>
          <div className="flex gap-5 text-[11px] text-[var(--subtle-text)]"><a href="#pricing" className="hover:text-[var(--text)]">Pricing</a><a href="#platform" className="hover:text-[var(--text)]">Platform</a></div>
        </div>
      </footer>
    </div>
  );
}
